import { ref } from 'vue'
import { httpGetJson } from '@/lib/net'
import {
  initialStylesheet,
  updateSource,
} from '@/services/layer-management-service'

export interface TilesetInfo {
  acquisition_start: string
  acquisition_end: string
  version: string
}

interface ImageryDateRange {
  start: string
  end: string
}

let infoFetched = false

const isLoading = ref<boolean>(false)
const startDate = ref<Date | null>(null)
const endDate = ref<Date | null>(null)
const selectedIndex = ref<number>(0)
const tilesetInfo = ref<TilesetInfo[]>([])

const HIGH_FREQ_SOURCE_ID = 'aerial-high-frequency'
const dateStart = 'acquisition_start'
const dateEnd = 'acquisition_end'

/**
 * *(AI-generated)*
 * Fetches recent imagery tileset info from the server.
 */
export const fetchRecentImageryInfo = async (): Promise<void> => {
  isLoading.value = true

  try {
    startDate.value = null
    endDate.value = null

    const response = await httpGetJson<TilesetInfo[]>(
      `${window.environment.tileserverUrl}/v1/aerial-high-frequency/tileset-info/limit/0`
    )

    if (!Array.isArray(response) || response.length === 0) {
      throw new Error('recent imagery response malformed')
    }

    // Toss out any bad data. This shouldn't happen, but can.
    const filteredResp = response.filter(
      (obj) => obj[dateStart] !== '' || obj[dateEnd] !== ''
    )

    tilesetInfo.value = filteredResp
    selectedIndex.value = 0
    updateDates(false)
  } finally {
    infoFetched = true
    isLoading.value = false
  }
}

/**
 * *(AI-generated)*
 * Returns the current selected imagery index.
 */
export const getIndex = (): number => selectedIndex.value

/**
 * *(AI-generated)*
 * Returns whether the imagery info has been fetched.
 */
export const getImageryInfoFetched = (): boolean => infoFetched

/**
 * *(AI-generated)*
 * Returns whether the imagery info is currently loading.
 */
export const getIsLoading = (): boolean => isLoading.value

/**
 * *(AI-generated)*
 * Returns the current imagery start date.
 */
export const getStartDate = (): Date | null => startDate.value

/**
 * *(AI-generated)*
 * Returns the current imagery end date.
 */
export const getEndDate = (): Date | null => endDate.value

/**
 * *(AI-generated)*
 * Returns whether a previous imagery set is available.
 */
export const canSetPrevious = (): boolean => {
  return selectedIndex.value < tilesetInfo.value.length - 1
}

/**
 * *(AI-generated)*
 * Returns whether a next imagery set is available.
 */
export const canSetNext = (): boolean => {
  return selectedIndex.value > 0
}

/**
 * *(AI-generated)*
 * Moves to the previous imagery dataset.
 */
export const setPrevious = (): void => {
  // Dataset is ordered newest to oldest
  selectedIndex.value++
  updateDates()
}

/**
 * *(AI-generated)*
 * Moves to the next imagery dataset.
 */
export const setNext = (): void => {
  // Dataset is ordered newest to oldest
  selectedIndex.value--
  updateDates()
}

/**
 * *(AI-generated)*
 * Sets the selected imagery index.
 */
export const setIndex = (index: number): boolean => {
  if (index < 0 || index >= tilesetInfo.value.length) {
    return false
  }
  selectedIndex.value = index
  updateDates()
  return true
}

const updateDates = (updateSource: boolean = true): void => {
  // Should never happen, just a safeguard
  if (!tilesetInfo.value.length) {
    startDate.value = null
    endDate.value = null
    return
  }

  const selectedInfo = tilesetInfo.value[selectedIndex.value]
  const startDateStr = selectedInfo[dateStart]
  const endDateStr = selectedInfo[dateEnd]

  try {
    startDate.value = startDateStr ? new Date(startDateStr) : null
    endDate.value = endDateStr ? new Date(endDateStr) : null
  } catch {
    // If string parsing fails, set state to default, so UI doesn't show wrong
    // dates.
    startDate.value = null
    endDate.value = null
  }

  if (updateSource) {
    updateHighFreqSource(selectedInfo['version'])
  }
}

const highFreqRe = /aerial-high-frequency\/v\d+/

/**
 * *(AI-generated)*
 * Updates the aerial high-frequency map source to the given version.
 */
export const updateHighFreqSource = (version: string): void => {
  const stylesheet = initialStylesheet()
  if (!stylesheet) {
    return
  }

  const freqSource = stylesheet.sources[HIGH_FREQ_SOURCE_ID]
  if (!freqSource?.tiles?.[0]) {
    return
  }

  const newTileUrl = freqSource.tiles[0].replace(
    highFreqRe,
    `aerial-high-frequency/v${version}`
  )
  freqSource.tiles[0] = newTileUrl

  // update source in stylesheet
  updateSource(HIGH_FREQ_SOURCE_ID, freqSource)
}

/**
 * *(AI-generated)*
 * Returns all available imagery date ranges.
 */
export const getAllImageryDateRanges = (): ImageryDateRange[] =>
  tilesetInfo.value.map((info) => ({
    start: info[dateStart],
    end: info[dateEnd],
  }))
